import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { AiOutlineDashboard } from "react-icons/ai";
import { FaFileArchive, FaFileInvoice } from "react-icons/fa";
import { FaCodePullRequest } from "react-icons/fa6";
import { GiStockpiles } from "react-icons/gi";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { LuLayoutDashboard } from "react-icons/lu";
import { MdSupportAgent } from "react-icons/md";
import { RiStockFill } from "react-icons/ri";

import { MwButton } from "../components/ui";
import CardHeadeWithActions from "../components/ui/CardHeadeWithActions";
import { useCurrent, useFetch, useInvoice, usePost, useTenant } from "../hooks";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
);

const normalizeCollection = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getLocalizedText = (items, preferredLang, fallback = "") => {
  if (!Array.isArray(items)) return fallback;

  const normalizedLang = String(preferredLang || "en")
    .toLowerCase()
    .startsWith("ar")
    ? "ar"
    : "en";
  const fallbackLang = normalizedLang === "ar" ? "en" : "ar";

  return (
    items.find((item) => item?.lang === normalizedLang)?.text ||
    items.find((item) => item?.lang === fallbackLang)?.text ||
    items.find((item) => item?.text)?.text ||
    fallback
  );
};

const toSafeNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getValidDate = (value) => {
  const parsedDate = value ? new Date(value) : null;
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
};

const buildMonthlySeries = (items, dateField, year) => {
  const totals = Array.from({ length: 12 }, () => 0);

  items.forEach((item) => {
    const itemDate = getValidDate(item?.[dateField]);

    if (!itemDate || itemDate.getFullYear() !== year) return;

    totals[itemDate.getMonth()] += 1;
  });

  return totals;
};

const sortByDateDesc = (items, dateField) => {
  return [...items].sort((leftItem, rightItem) => {
    const leftDate = getValidDate(leftItem?.[dateField])?.getTime() || 0;
    const rightDate = getValidDate(rightItem?.[dateField])?.getTime() || 0;
    return rightDate - leftDate;
  });
};

const DashboardSurface = ({ children, className = "" }) => {
  return (
    <div
      className={`rounded-[28px] border border-slate-200/80 bg-white shadow-[0_30px_80px_-48px_rgba(37,22,88,0.45)] ${className}`}
    >
      {children}
    </div>
  );
};

const ChartEmptyState = ({ message, className = "" }) => {
  return (
    <div
      className={`flex h-full min-h-[210px] items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50/90 p-6 text-center text-sm text-slate-500 md:min-h-[230px] xl:min-h-[250px] ${className}`}
    >
      {message}
    </div>
  );
};

const ProgressMetric = ({
  label,
  value,
  total,
  colorClass,
  loading,
  summary,
}) => {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50/90 p-4">
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="text-sm font-medium text-slate-600">{label}</span>
        {loading ? (
          <div className="h-5 w-20 animate-pulse rounded-full bg-slate-200" />
        ) : (
          <span className="text-sm font-semibold text-slate-900">{summary}</span>
        )}
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full transition-all duration-700 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="mt-2 text-xs text-slate-500">{percentage}%</div>
    </div>
  );
};

const DashboardStatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  hint,
  href,
  loading,
  tintClass,
}) => {
  const cardContent = (
    <DashboardSurface className="h-full p-5 transition-transform duration-300 hover:-translate-y-1">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-500">{title}</div>

          {loading ? (
            <div className="mt-3 h-9 w-24 animate-pulse rounded-2xl bg-slate-200" />
          ) : (
            <div className="mt-3 text-3xl font-black tracking-tight text-slate-900">
              {value}
            </div>
          )}

          <div className="mt-2 text-sm text-slate-500">{subtitle}</div>
        </div>

        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg ${tintClass}`}
        >
          <Icon size={22} />
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4 text-xs">
        <span className="text-slate-500">{hint}</span>
        {href && <span className="font-semibold text-[#251658]">↗</span>}
      </div>
    </DashboardSurface>
  );

  if (!href) return cardContent;

  return <Link to={href}>{cardContent}</Link>;
};

const InsightTile = ({ label, value, subtitle }) => {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50/90 p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </div>
      <div className="mt-2 text-lg font-bold text-slate-900">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{subtitle}</div>
    </div>
  );
};

const ActivityRow = ({ title, subtitle, badge, badgeClassName, date, to, cta }) => {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3 transition-all duration-300 hover:border-[#d9d2ef] hover:bg-white hover:shadow-sm"
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-bold text-slate-900">{title}</div>
        <div className="truncate text-sm text-slate-500">{subtitle}</div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${badgeClassName}`}
        >
          {badge}
        </span>
        <div className="text-xs text-slate-400">{date}</div>
        <span className="text-sm font-semibold text-[#251658]">{cta}</span>
      </div>
    </Link>
  );
};

const DashBoard = () => {
  const [t, i18n] = useTranslation("global");
  const params = useParams();
  const { formatter } = useInvoice();
  const { tenantUsername, companySelected, branchSelected } = useTenant();
  const { currentTenantId, currentCompanyId, currentBranchId } = useCurrent();
  const {
    data: warehousesResponse,
    loading: warehousesLoading,
    error: warehousesError,
  } = useFetch(
    `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/warehouses/${currentTenantId}/${currentCompanyId}/${currentBranchId}`,
  );
  const {
    data: stocktakingsResponse,
    loading: stocktakingsLoading,
    error: stocktakingsError,
  } = useFetch(
    `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/stocktaking/master/${currentTenantId}/${currentCompanyId}/${currentBranchId}`,
  );
  const {
    data: requisitionsResponse,
    loading: requisitionsLoading,
    error: requisitionsError,
  } = useFetch(
    `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/requisitions/master/${currentTenantId}/${currentCompanyId}/${currentBranchId}`,
  );
  const {
    data: pendingPurchasesResponse,
    loading: pendingPurchasesLoading,
    error: pendingPurchasesError,
  } = useFetch(
    `${process.env.REACT_APP_INV_API_PURCHASES_BASE_URL}/invoices/not-delivered/${currentTenantId}/${currentCompanyId}/${currentBranchId}`,
  );
  const {
    data: pendingSalesResponse,
    loading: pendingSalesLoading,
    error: pendingSalesError,
  } = useFetch(
    `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/invoices/not-delivered/${currentTenantId}/${currentCompanyId}/${currentBranchId}`,
  );
  const {
    data: productsResponse,
    loading: productsLoading,
    error: productsError,
    postData: fetchProducts,
  } = usePost();
  const fetchProductsRef = useRef(fetchProducts);

  useEffect(() => {
    fetchProductsRef.current = fetchProducts;
  }, [fetchProducts]);

  useEffect(() => {
    if (!currentTenantId || !currentCompanyId) return;

    fetchProductsRef.current(
      `${process.env.REACT_APP_PRODUCTS_API_SERVER_BASE_URL}/search/All`,
      {
        tenantId: currentTenantId,
        companyId: currentCompanyId,
      },
    );
  }, [currentCompanyId, currentTenantId]);

  const effectiveTenant = params.tenant || tenantUsername;
  const token = localStorage.getItem("token");
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const locale = String(i18n.language || "en")
    .toLowerCase()
    .startsWith("ar")
    ? "ar-EG"
    : "en-US";
  const isArabic = locale.startsWith("ar");

  const content = isArabic
    ? {
        heroTitle: "مركز قيادة المخزون",
        heroDescription:
          "متابعة سريعة لأداء المستودعات، الأذونات، أوامر الاستلام، والجرد من شاشة واحدة.",
        liveBadge: "بيانات تشغيلية مباشرة",
        liveDescription:
          "مبنية على الوحدات الفعلية المرتبطة بالشركة والفرع الحاليين.",
        company: "الشركة",
        branch: "الفرع",
        currentMonth: "هذا الشهر",
        support: "الدعم الفني",
        operationalPulse: "نبض التشغيل",
        pulseDescription: "مؤشرات سريعة على جاهزية التشغيل الحالية.",
        activeWarehouses: "المستودعات النشطة",
        settledStocktakings: "تسوية الجرد",
        pendingValue: "قيمة الوثائق المعلقة",
        catalogCoverage: "نشاط كتالوج المنتجات",
        active: "نشط",
        settled: "تمت التسوية",
        pendingDocs: "وثائق مفتوحة",
        warningLabel: "تنبيه",
        noChartData: "لا توجد بيانات كافية لعرض هذا الرسم حاليًا.",
        noRecentData: "لا توجد عناصر حديثة حتى الآن.",
        openItem: "فتح",
        unknownWarehouse: "مستودع غير محدد",
        uncategorized: "غير مصنف",
        categoryUnit: "منتج",
        activityUnit: "عملية",
        pendingValueNote: "إجمالي أوامر بانتظار المعالجة",
        trendTitle: "اتجاه العمليات خلال السنة",
        trendDescription:
          "يركز على عدد الأذونات والجرد والوثائق المفتوحة شهريًا.",
        mixTitle: "توزيع أنواع الأذونات",
        mixDescription: "يعكس طبيعة الحركة بين استلام وصرف وتحويل.",
        categoryTitle: "أعلى تصنيفات المنتجات",
        categoryDescription: "أكثر التصنيفات حضورًا داخل كتالوج الشركة.",
        insightsTitle: "نقاط تركيز سريعة",
        insightsDescription:
          "ملخص تنفيذي يساعدك تعرف أين تتابع أولًا.",
        busiestWarehouse: "الأكثر نشاطًا",
        topCategory: "أكبر تصنيف",
        openDocuments: "مستندات مفتوحة",
        quickLinks: "اختصارات سريعة",
        recentRequisitions: "آخر الأذونات",
        recentStocktakings: "آخر عمليات الجرد",
        recentDescription: "أقرب العمليات المضافة أو المحدثة مؤخرًا.",
        quickLinkDescriptions: {
          warehouses: "عرض المستودعات والتنقل إلى التفاصيل.",
          products: "إدارة الأصناف والتصنيفات من نفس المكان.",
          requisitions: "متابعة حركات الاستلام والصرف والتحويل.",
          reports: "الانتقال للتقارير التفصيلية والتحليلات.",
        },
        typeLabels: {
          PUSH: "استلام",
          PULL: "صرف",
          TRANS: "تحويل",
        },
        trendLabels: {
          requisitions: "الأذونات",
          stocktakings: "الجرد",
          pendingDocs: "وثائق معلقة",
        },
      }
    : {
        heroTitle: "Inventory Command Center",
        heroDescription:
          "A fast operating view for warehouses, requisitions, receipt orders, and stocktakings from one place.",
        liveBadge: "Live operational data",
        liveDescription:
          "Built from the modules currently connected to the selected company and branch.",
        company: "Company",
        branch: "Branch",
        currentMonth: "This month",
        support: "Support",
        operationalPulse: "Operational pulse",
        pulseDescription: "Quick indicators for current operating readiness.",
        activeWarehouses: "Active warehouses",
        settledStocktakings: "Stocktaking settlement",
        pendingValue: "Pending documents value",
        catalogCoverage: "Product catalog activity",
        active: "Active",
        settled: "Settled",
        pendingDocs: "Open documents",
        warningLabel: "warning",
        noChartData: "There is not enough data to render this chart yet.",
        noRecentData: "No recent activity yet.",
        openItem: "Open",
        unknownWarehouse: "Unknown warehouse",
        uncategorized: "Uncategorized",
        categoryUnit: "products",
        activityUnit: "activities",
        pendingValueNote: "Total orders waiting for processing",
        trendTitle: "Operations trend this year",
        trendDescription:
          "Tracks requisitions, stocktakings, and open documents by month.",
        mixTitle: "Requisition type split",
        mixDescription: "Shows how movement is split across receipt, issue, and transfer.",
        categoryTitle: "Top product categories",
        categoryDescription: "The most represented categories in the company catalog.",
        insightsTitle: "Quick focus points",
        insightsDescription:
          "An executive snapshot to help you decide where to look first.",
        busiestWarehouse: "Busiest warehouse",
        topCategory: "Largest category",
        openDocuments: "Open documents",
        quickLinks: "Quick links",
        recentRequisitions: "Latest requisitions",
        recentStocktakings: "Latest stocktakings",
        recentDescription: "The nearest records added or updated recently.",
        quickLinkDescriptions: {
          warehouses: "Open warehouse records and jump into details.",
          products: "Manage items and classifications in one place.",
          requisitions: "Follow receipt, issue, and transfer flows.",
          reports: "Open detailed reports and operational analysis.",
        },
        typeLabels: {
          PUSH: "Receipt",
          PULL: "Issue",
          TRANS: "Transfer",
        },
        trendLabels: {
          requisitions: "Requisitions",
          stocktakings: "Stocktakings",
          pendingDocs: "Pending docs",
        },
      };

  const resolveTenantRoute = (path) => {
    if (!effectiveTenant) return "/notfound";
    return `/${effectiveTenant}${path}`;
  };

  const supportUrl =
    process.env.REACT_APP_WEB_SUPPORT_BASE_URL && effectiveTenant && token
      ? `${process.env.REACT_APP_WEB_SUPPORT_BASE_URL}/${effectiveTenant}/token/${token}`
      : null;

  const warehouses = normalizeCollection(warehousesResponse);
  const stocktakings = normalizeCollection(stocktakingsResponse);
  const requisitions = normalizeCollection(requisitionsResponse);
  const pendingPurchases = normalizeCollection(pendingPurchasesResponse);
  const pendingSales = normalizeCollection(pendingSalesResponse);
  const products = normalizeCollection(productsResponse);

  const activeWarehousesCount = warehouses.filter((item) => item?.is_active).length;
  const stocktakingsSettledCount = stocktakings.filter((item) => item?.completed).length;
  const activeProductsCount = products.filter((item) => item?.isActive).length;
  const requisitionTypeCounts = {
    PUSH: requisitions.filter((item) => item?.req_type === "PUSH").length,
    PULL: requisitions.filter((item) => item?.req_type === "PULL").length,
    TRANS: requisitions.filter((item) => item?.req_type === "TRANS").length,
  };

  const pendingPurchasesValue = pendingPurchases.reduce(
    (sum, item) => sum + toSafeNumber(item?.net_amount),
    0,
  );
  const pendingSalesValue = pendingSales.reduce(
    (sum, item) => sum + toSafeNumber(item?.net_amount),
    0,
  );
  const pendingDocumentsCount = pendingPurchases.length + pendingSales.length;
  const pendingDocumentsValue = pendingPurchasesValue + pendingSalesValue;

  const monthLabels = Array.from({ length: 12 }, (_, monthIndex) =>
    new Intl.DateTimeFormat(locale, { month: "short" }).format(
      new Date(currentYear, monthIndex, 1),
    ),
  );
  const requisitionsMonthly = buildMonthlySeries(requisitions, "req_date", currentYear);
  const stocktakingsMonthly = buildMonthlySeries(
    stocktakings,
    "stocktaking_at",
    currentYear,
  );
  const pendingPurchasesMonthly = buildMonthlySeries(
    pendingPurchases,
    "inv_date",
    currentYear,
  );
  const pendingSalesMonthly = buildMonthlySeries(
    pendingSales,
    "inv_date",
    currentYear,
  );
  const pendingDocumentsMonthly = monthLabels.map(
    (_, index) => pendingPurchasesMonthly[index] + pendingSalesMonthly[index],
  );
  const currentMonthSummary = {
    requisitions: requisitionsMonthly[currentMonthIndex] || 0,
    stocktakings: stocktakingsMonthly[currentMonthIndex] || 0,
    pendingDocs: pendingDocumentsMonthly[currentMonthIndex] || 0,
  };

  const categoryBuckets = {};
  products.forEach((product) => {
    const categoryName = getLocalizedText(
      product?.productCategory?.[0]?.categoryName,
      i18n.language,
      content.uncategorized,
    );
    categoryBuckets[categoryName] = (categoryBuckets[categoryName] || 0) + 1;
  });
  const categoryItems = Object.entries(categoryBuckets)
    .sort((leftItem, rightItem) => rightItem[1] - leftItem[1])
    .slice(0, 6);

  const warehouseActivityBuckets = {};
  requisitions.forEach((item) => {
    const warehouseName =
      item?.warehouse?.name_ar ||
      item?.warehouse?.name_en ||
      item?.warehouse?.name ||
      content.unknownWarehouse;
    warehouseActivityBuckets[warehouseName] =
      (warehouseActivityBuckets[warehouseName] || 0) + 1;
  });
  stocktakings.forEach((item) => {
    const warehouseName =
      item?.warehouse?.name_ar ||
      item?.warehouse?.name_en ||
      item?.warehouse?.name ||
      content.unknownWarehouse;
    warehouseActivityBuckets[warehouseName] =
      (warehouseActivityBuckets[warehouseName] || 0) + 1;
  });

  const warehouseActivityItems = Object.entries(warehouseActivityBuckets).sort(
    (leftItem, rightItem) => rightItem[1] - leftItem[1],
  );
  const busiestWarehouse = warehouseActivityItems[0] || [
    content.unknownWarehouse,
    0,
  ];
  const largestCategory = categoryItems[0] || [content.uncategorized, 0];

  const latestRequisitions = sortByDateDesc(requisitions, "req_date").slice(0, 4);
  const latestStocktakings = sortByDateDesc(stocktakings, "stocktaking_at").slice(
    0,
    4,
  );

  const hasTrendData =
    requisitionsMonthly.some(Boolean) ||
    stocktakingsMonthly.some(Boolean) ||
    pendingDocumentsMonthly.some(Boolean);
  const hasMixData = Object.values(requisitionTypeCounts).some(Boolean);
  const hasCategoryData = categoryItems.length > 0;

  const anyDataLoaded =
    warehouses.length > 0 ||
    stocktakings.length > 0 ||
    requisitions.length > 0 ||
    pendingPurchases.length > 0 ||
    pendingSales.length > 0 ||
    products.length > 0;

  const chartLegendOptions = {
    position: "bottom",
    rtl: isArabic,
    labels: {
      usePointStyle: true,
      boxWidth: 10,
      color: "#475569",
      font: {
        family: '"Tajawal", sans-serif',
        size: 12,
      },
    },
  };

  const axisTickOptions = {
    color: "#64748b",
    font: {
      family: '"Tajawal", sans-serif',
      size: 11,
    },
  };

  const lineChartData = {
    labels: monthLabels,
    datasets: [
      {
        label: content.trendLabels.requisitions,
        data: requisitionsMonthly,
        borderColor: "#251658",
        backgroundColor: "rgba(37,22,88,0.08)",
        fill: true,
        pointBackgroundColor: "#251658",
        pointRadius: 3,
        tension: 0.35,
      },
      {
        label: content.trendLabels.stocktakings,
        data: stocktakingsMonthly,
        borderColor: "#699635",
        backgroundColor: "rgba(105,150,53,0.08)",
        fill: true,
        pointBackgroundColor: "#699635",
        pointRadius: 3,
        tension: 0.35,
      },
      {
        label: content.trendLabels.pendingDocs,
        data: pendingDocumentsMonthly,
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245,158,11,0.08)",
        fill: true,
        pointBackgroundColor: "#f59e0b",
        pointRadius: 3,
        tension: 0.35,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: chartLegendOptions,
    },
    scales: {
      x: {
        ticks: axisTickOptions,
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: axisTickOptions,
        grid: {
          color: "rgba(148,163,184,0.18)",
        },
      },
    },
  };

  const mixChartData = {
    labels: [
      content.typeLabels.PUSH,
      content.typeLabels.PULL,
      content.typeLabels.TRANS,
    ],
    datasets: [
      {
        data: [
          requisitionTypeCounts.PUSH,
          requisitionTypeCounts.PULL,
          requisitionTypeCounts.TRANS,
        ],
        backgroundColor: ["#699635", "#251658", "#f59e0b"],
        borderColor: ["#699635", "#251658", "#f59e0b"],
        borderWidth: 1,
      },
    ],
  };

  const mixChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: chartLegendOptions,
    },
  };

  const categoryChartData = {
    labels: categoryItems.map(([label]) => label),
    datasets: [
      {
        data: categoryItems.map(([, value]) => value),
        backgroundColor: [
          "#251658",
          "#3b2c78",
          "#4e3c91",
          "#699635",
          "#84ad4b",
          "#f59e0b",
        ],
        borderRadius: 10,
        borderSkipped: false,
      },
    ],
  };

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: axisTickOptions,
        grid: {
          color: "rgba(148,163,184,0.18)",
        },
      },
      y: {
        ticks: axisTickOptions,
        grid: {
          display: false,
        },
      },
    },
  };

  const formatDate = (value) => {
    const parsedDate = getValidDate(value);
    if (!parsedDate) return "—";
    return parsedDate.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const supportButton = supportUrl ? (
    <Link target="_blank" to={supportUrl}>
      <MwButton classNameCustom="w-full md:w-auto" inGroup={true} type="saveBtn">
        <MdSupportAgent size={18} />
        {content.support}
      </MwButton>
    </Link>
  ) : null;

  const summaryCards = [
    {
      icon: RiStockFill,
      title: t("menu.warehouse_management"),
      value: new Intl.NumberFormat(locale).format(warehouses.length),
      subtitle: `${activeWarehousesCount}/${warehouses.length || 0} ${content.active}`,
      hint: content.quickLinkDescriptions.warehouses,
      href: resolveTenantRoute("/warehouses"),
      loading: warehousesLoading,
      tintClass: "from-[#699635] to-[#87b84a]",
    },
    {
      icon: LuLayoutDashboard,
      title: t("menu.Products"),
      value: new Intl.NumberFormat(locale).format(products.length),
      subtitle: `${activeProductsCount}/${products.length || 0} ${content.active}`,
      hint: content.quickLinkDescriptions.products,
      href: resolveTenantRoute("/products-home"),
      loading: productsLoading,
      tintClass: "from-[#251658] to-[#4d3591]",
    },
    {
      icon: FaCodePullRequest,
      title: t("menu.requisition_management"),
      value: new Intl.NumberFormat(locale).format(requisitions.length),
      subtitle: `${content.typeLabels.PUSH} ${requisitionTypeCounts.PUSH} • ${content.typeLabels.PULL} ${requisitionTypeCounts.PULL} • ${content.typeLabels.TRANS} ${requisitionTypeCounts.TRANS}`,
      hint: content.quickLinkDescriptions.requisitions,
      href: resolveTenantRoute("/requisition"),
      loading: requisitionsLoading,
      tintClass: "from-[#2f1965] to-[#699635]",
    },
    {
      icon: GiStockpiles,
      title: t("menu.inventory_management"),
      value: new Intl.NumberFormat(locale).format(stocktakings.length),
      subtitle: `${stocktakingsSettledCount}/${stocktakings.length || 0} ${content.settled}`,
      hint: content.recentDescription,
      href: resolveTenantRoute("/stocktakings"),
      loading: stocktakingsLoading,
      tintClass: "from-[#0f766e] to-[#14b8a6]",
    },
    {
      icon: FaFileInvoice,
      title: t("menu.purchase_orders"),
      value: new Intl.NumberFormat(locale).format(pendingPurchases.length),
      subtitle: formatter.format(pendingPurchasesValue),
      hint: content.pendingValueNote,
      href: resolveTenantRoute("/invoice-requisition"),
      loading: pendingPurchasesLoading,
      tintClass: "from-[#b45309] to-[#f59e0b]",
    },
    {
      icon: FaFileArchive,
      title: t("menu.sales_order"),
      value: new Intl.NumberFormat(locale).format(pendingSales.length),
      subtitle: formatter.format(pendingSalesValue),
      hint: content.pendingValueNote,
      href: resolveTenantRoute("/invoice-sales-requisition"),
      loading: pendingSalesLoading,
      tintClass: "from-[#1d4ed8] to-[#60a5fa]",
    },
  ];

  const quickLinks = [
    {
      icon: RiStockFill,
      label: t("menu.warehouse_management"),
      to: resolveTenantRoute("/warehouses"),
      count: warehouses.length,
      description: content.quickLinkDescriptions.warehouses,
      tintClass: "bg-[#eef6e4] text-[#699635]",
    },
    {
      icon: LuLayoutDashboard,
      label: t("menu.Products"),
      to: resolveTenantRoute("/products-home"),
      count: products.length,
      description: content.quickLinkDescriptions.products,
      tintClass: "bg-[#ede8fb] text-[#251658]",
    },
    {
      icon: FaCodePullRequest,
      label: t("menu.requisition_management"),
      to: resolveTenantRoute("/requisition"),
      count: requisitions.length,
      description: content.quickLinkDescriptions.requisitions,
      tintClass: "bg-[#eef2ff] text-[#2f1965]",
    },
    {
      icon: HiOutlineDocumentReport,
      label: t("menu.reports"),
      to: resolveTenantRoute("/reports"),
      count: pendingDocumentsCount,
      description: content.quickLinkDescriptions.reports,
      tintClass: "bg-[#fff3e5] text-[#b45309]",
    },
  ];

  const operationalErrors = [
    warehousesError,
    stocktakingsError,
    requisitionsError,
    pendingPurchasesError,
    pendingSalesError,
    productsError,
  ].filter(Boolean).length;

  return (
    <div className="flex h-full flex-col">
      <CardHeadeWithActions
        title={t("menu.Dashboard")}
        icon={<AiOutlineDashboard size={18} />}
      >
        {supportButton}
      </CardHeadeWithActions>

      <div className="flex-1 overflow-y-auto bg-slate-50 p-4 md:p-6">
        <div className="mx-auto flex max-w-7xl flex-col gap-6">
          <div className="grid gap-6 xl:grid-cols-[1.45fr_0.95fr]">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <DashboardSurface className="relative overflow-hidden bg-gradient-to-br from-[#251658] via-[#342067] to-[#699635] p-6 text-white">
                <div className="absolute -right-8 -top-8 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
                <div className="absolute bottom-0 left-0 h-28 w-28 rounded-full bg-black/10 blur-2xl" />

                <div className="relative z-10">
                  <div className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold">
                    {content.liveBadge}
                  </div>

                  <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-2xl">
                      <div className="text-3xl font-black tracking-tight md:text-4xl">
                        {content.heroTitle}
                      </div>
                      <div className="mt-3 text-sm leading-7 text-white/80 md:text-base">
                        {content.heroDescription}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white/85 backdrop-blur-sm">
                      <div className="font-semibold">
                        {currentDate.toLocaleDateString(locale, {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </div>
                      <div className="mt-1 text-white/70">{content.liveDescription}</div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <div className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm">
                      <span className="text-white/65">{content.company}: </span>
                      <span className="font-semibold">
                        {companySelected?.name || "—"}
                      </span>
                    </div>
                    <div className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm">
                      <span className="text-white/65">{content.branch}: </span>
                      <span className="font-semibold">
                        {branchSelected?.name || "—"}
                      </span>
                    </div>
                    <div className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm">
                      <span className="text-white/65">{content.currentMonth}: </span>
                      <span className="font-semibold">
                        {monthLabels[currentMonthIndex]}
                      </span>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                      <div className="text-xs text-white/65">
                        {content.trendLabels.requisitions}
                      </div>
                      <div className="mt-2 text-2xl font-black">
                        {new Intl.NumberFormat(locale).format(
                          currentMonthSummary.requisitions,
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                      <div className="text-xs text-white/65">
                        {content.trendLabels.stocktakings}
                      </div>
                      <div className="mt-2 text-2xl font-black">
                        {new Intl.NumberFormat(locale).format(
                          currentMonthSummary.stocktakings,
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                      <div className="text-xs text-white/65">
                        {content.trendLabels.pendingDocs}
                      </div>
                      <div className="mt-2 text-2xl font-black">
                        {new Intl.NumberFormat(locale).format(
                          currentMonthSummary.pendingDocs,
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </DashboardSurface>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
            >
              <DashboardSurface className="h-full p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xl font-bold text-slate-900">
                      {content.operationalPulse}
                    </div>
                    <div className="mt-2 text-sm leading-6 text-slate-500">
                      {content.pulseDescription}
                    </div>
                  </div>

                    <div className="rounded-full bg-[#f2eefb] px-3 py-1 text-xs font-semibold text-[#251658]">
                      {operationalErrors > 0
                      ? `${operationalErrors} ${content.warningLabel}`
                      : anyDataLoaded
                        ? content.liveBadge
                        : content.pendingDocs}
                  </div>
                </div>

                <div className="mt-6 grid gap-4">
                  <ProgressMetric
                    label={content.activeWarehouses}
                    value={activeWarehousesCount}
                    total={warehouses.length}
                    colorClass="bg-gradient-to-r from-[#699635] to-[#89b64d]"
                    loading={warehousesLoading}
                    summary={`${activeWarehousesCount}/${warehouses.length || 0}`}
                  />

                  <ProgressMetric
                    label={content.settledStocktakings}
                    value={stocktakingsSettledCount}
                    total={stocktakings.length}
                    colorClass="bg-gradient-to-r from-[#251658] to-[#4d3591]"
                    loading={stocktakingsLoading}
                    summary={`${stocktakingsSettledCount}/${stocktakings.length || 0}`}
                  />

                  <ProgressMetric
                    label={content.catalogCoverage}
                    value={activeProductsCount}
                    total={products.length}
                    colorClass="bg-gradient-to-r from-[#f59e0b] to-[#fbbf24]"
                    loading={productsLoading}
                    summary={`${activeProductsCount}/${products.length || 0}`}
                  />
                </div>

                <div className="mt-6 rounded-[24px] border border-slate-100 bg-slate-50 p-5">
                  <div className="text-sm text-slate-500">{content.pendingValue}</div>
                  <div className="mt-2 text-3xl font-black text-slate-900">
                    {pendingPurchasesLoading || pendingSalesLoading
                      ? "…"
                      : formatter.format(pendingDocumentsValue)}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {pendingDocumentsCount} {content.pendingDocs}
                  </div>
                </div>
              </DashboardSurface>
            </motion.div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {summaryCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.06 * index }}
              >
                <DashboardStatCard {...card} />
              </motion.div>
            ))}
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.35fr_0.95fr]">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.14 }}
            >
              <DashboardSurface className="flex h-full flex-col p-6">
                <div className="mb-5">
                  <div className="text-xl font-bold text-slate-900">
                    {content.trendTitle}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {content.trendDescription}
                  </div>
                </div>

                {hasTrendData ? (
                  <div className="h-[240px] md:h-[280px] xl:h-[300px]">
                    <Line data={lineChartData} options={lineChartOptions} />
                  </div>
                ) : (
                  <ChartEmptyState message={content.noChartData} />
                )}
              </DashboardSurface>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <DashboardSurface className="flex h-full flex-col p-6">
                <div className="mb-5">
                  <div className="text-xl font-bold text-slate-900">
                    {content.mixTitle}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {content.mixDescription}
                  </div>
                </div>

                {hasMixData ? (
                  <div className="flex h-[240px] flex-col md:h-[280px] xl:h-[300px]">
                    <div className="flex-1">
                      <Doughnut data={mixChartData} options={mixChartOptions} />
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3">
                      {[
                        {
                          label: content.typeLabels.PUSH,
                          value: requisitionTypeCounts.PUSH,
                          className: "bg-[#eef6e4] text-[#699635]",
                        },
                        {
                          label: content.typeLabels.PULL,
                          value: requisitionTypeCounts.PULL,
                          className: "bg-[#ede8fb] text-[#251658]",
                        },
                        {
                          label: content.typeLabels.TRANS,
                          value: requisitionTypeCounts.TRANS,
                          className: "bg-[#fff4df] text-[#b45309]",
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className={`rounded-2xl px-4 py-3 text-center ${item.className}`}
                        >
                          <div className="text-xs font-semibold">{item.label}</div>
                          <div className="mt-1 text-xl font-black">{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <ChartEmptyState message={content.noChartData} />
                )}
              </DashboardSurface>
            </motion.div>
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.26 }}
            >
              <DashboardSurface className="flex h-full flex-col p-6">
                <div className="mb-5">
                  <div className="text-xl font-bold text-slate-900">
                    {content.categoryTitle}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {content.categoryDescription}
                  </div>
                </div>

                {hasCategoryData ? (
                  <div className="h-[220px] md:h-[250px] xl:h-[270px]">
                    <Bar data={categoryChartData} options={categoryChartOptions} />
                  </div>
                ) : (
                  <ChartEmptyState message={content.noChartData} />
                )}
              </DashboardSurface>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.32 }}
            >
              <DashboardSurface className="flex h-full flex-col p-6">
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {content.insightsTitle}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {content.insightsDescription}
                  </div>
                </div>

                <div className="mt-5 grid gap-3">
                  <InsightTile
                    label={content.busiestWarehouse}
                    value={busiestWarehouse[0]}
                    subtitle={`${new Intl.NumberFormat(locale).format(
                      busiestWarehouse[1],
                    )} ${content.activityUnit}`}
                  />
                  <InsightTile
                    label={content.topCategory}
                    value={largestCategory[0]}
                    subtitle={`${new Intl.NumberFormat(locale).format(
                      largestCategory[1],
                    )} ${content.categoryUnit}`}
                  />
                  <InsightTile
                    label={content.openDocuments}
                    value={new Intl.NumberFormat(locale).format(pendingDocumentsCount)}
                    subtitle={formatter.format(pendingDocumentsValue)}
                  />
                </div>

                <div className="mt-6 border-t border-slate-100 pt-6 xl:mt-auto">
                  <div className="mb-4 text-sm font-bold text-slate-900">
                    {content.quickLinks}
                  </div>

                  <div className="grid gap-3">
                    {quickLinks.map((linkItem) => {
                      const LinkIcon = linkItem.icon;

                      return (
                        <Link
                          key={linkItem.label}
                          to={linkItem.to}
                          className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3 transition-all duration-300 hover:border-[#d9d2ef] hover:bg-white"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`flex h-11 w-11 items-center justify-center rounded-2xl ${linkItem.tintClass}`}
                            >
                              <LinkIcon size={20} />
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">
                                {linkItem.label}
                              </div>
                              <div className="text-sm text-slate-500">
                                {linkItem.description}
                              </div>
                            </div>
                          </div>

                          <div className="text-sm font-bold text-[#251658]">
                            {new Intl.NumberFormat(locale).format(linkItem.count)}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </DashboardSurface>
            </motion.div>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.38 }}
            >
              <DashboardSurface className="p-6">
                <div className="mb-5">
                  <div className="text-xl font-bold text-slate-900">
                    {content.recentRequisitions}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {content.recentDescription}
                  </div>
                </div>

                <div className="grid gap-3">
                  {requisitionsLoading && latestRequisitions.length === 0
                    ? Array.from({ length: 4 }, (_, index) => (
                        <div
                          key={`requisition-skeleton-${index}`}
                          className="h-20 animate-pulse rounded-2xl bg-slate-100"
                        />
                      ))
                    : latestRequisitions.map((item) => (
                        <ActivityRow
                          key={item?.id}
                          title={`#${item?.req_no || "—"}`}
                          subtitle={
                            item?.warehouse?.name_ar ||
                            item?.warehouse?.name_en ||
                            content.unknownWarehouse
                          }
                          badge={content.typeLabels[item?.req_type] || item?.req_type || "—"}
                          badgeClassName={
                            item?.req_type === "PUSH"
                              ? "bg-[#eef6e4] text-[#699635]"
                              : item?.req_type === "PULL"
                                ? "bg-[#ede8fb] text-[#251658]"
                                : "bg-[#fff4df] text-[#b45309]"
                          }
                          date={formatDate(item?.req_date)}
                          to={resolveTenantRoute(`/requisition-view/${item?.id}`)}
                          cta={content.openItem}
                        />
                      ))}

                  {!requisitionsLoading && latestRequisitions.length === 0 && (
                    <ChartEmptyState message={content.noRecentData} />
                  )}
                </div>
              </DashboardSurface>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.44 }}
            >
              <DashboardSurface className="p-6">
                <div className="mb-5">
                  <div className="text-xl font-bold text-slate-900">
                    {content.recentStocktakings}
                  </div>
                  <div className="mt-2 text-sm text-slate-500">
                    {content.recentDescription}
                  </div>
                </div>

                <div className="grid gap-3">
                  {stocktakingsLoading && latestStocktakings.length === 0
                    ? Array.from({ length: 4 }, (_, index) => (
                        <div
                          key={`stocktaking-skeleton-${index}`}
                          className="h-20 animate-pulse rounded-2xl bg-slate-100"
                        />
                      ))
                    : latestStocktakings.map((item) => (
                        <ActivityRow
                          key={item?.id}
                          title={item?.code || "—"}
                          subtitle={
                            item?.warehouse?.name_ar ||
                            item?.warehouse?.name_en ||
                            content.unknownWarehouse
                          }
                          badge={
                            item?.completed
                              ? t("stocktaking.Settlement_Completed")
                              : t("stocktaking.Settlement_Pending")
                          }
                          badgeClassName={
                            item?.completed
                              ? "bg-[#eef6e4] text-[#699635]"
                              : "bg-slate-200 text-slate-600"
                          }
                          date={formatDate(item?.stocktaking_at)}
                          to={resolveTenantRoute(`/stocktakings/view-table/${item?.id}`)}
                          cta={content.openItem}
                        />
                      ))}

                  {!stocktakingsLoading && latestStocktakings.length === 0 && (
                    <ChartEmptyState message={content.noRecentData} />
                  )}
                </div>
              </DashboardSurface>
            </motion.div>
          </div>

          {!anyDataLoaded && !warehousesLoading && !productsLoading && (
            <DashboardSurface className="p-6">
              <div className="text-center">
                <div className="text-xl font-bold text-slate-900">
                  {content.liveBadge}
                </div>
                <div className="mt-3 text-sm text-slate-500">
                  {content.noChartData}
                </div>
              </div>
            </DashboardSurface>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashBoard;
